import { ConversationRole } from '@cycle-app/graphql-codegen';
import { InfiniteScroll, Skeleton } from '@cycle-app/ui';
import { AiIcon } from '@cycle-app/ui/icons';
import { nodeToArray } from '@cycle-app/utilities';
import { format, parseISO } from 'date-fns';
import { ReactMarkdown } from 'react-markdown/lib/react-markdown';
import scrollIntoView from 'scroll-into-view-if-needed';

import { useConversation } from '../../hooks/ask/useConversation';
import { useConversationAsk } from '../../hooks/ask/useConversationAsk';
import { Question, QuestionDate, QuestionInput } from '../Ask/Ask.styles';

type Props = {
  conversationId: string;
  docId?: string;
  onAskNew: VoidFunction;
};

export const AskConversation = ({
  conversationId, docId, onAskNew,
}: Props) => {
  const {
    conversation, isLoading, isLoadingMore, hasNextPage, loadMore,
  } = useConversation(conversationId);
  const { ask } = useConversationAsk();
  const messages = nodeToArray(conversation?.messages);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-4 px-5">
        <Skeleton className="w-20! h-3.5! flex-none" />
        <Skeleton className="h-8!" />
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="grow flex justify-center items-center">
        <div className="text-center">
          <AiIcon className="text-grey-400" />
          <div className="text-secondary mt-3.5">
            Conversation not found
          </div>
          <button
            type="button"
            onClick={onAskNew}
            className="btn-tertiary text-cycle dark:text-white mt-5"
          >
            New
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="grow relative flex flex-col justify-between overflow-hidden">
      <InfiniteScroll
        isLoading={isLoading || isLoadingMore}
        hasMoreData={hasNextPage}
        loadMore={loadMore}
        className="shy-scrollbar grow"
      >
        <ul className="flex flex-col gap-5 px-5">
          <li>
            <div className="flex flex-col items-center gap-4">
              <QuestionDate>
                {format(parseISO(conversation.createdAt), 'PP')}
              </QuestionDate>
              <Question>
                {conversation.content}
              </Question>
            </div>
          </li>
          {messages.map(message => (
            <li
              key={message.id}
              data-id={message.id}
            >
              {message.role === ConversationRole.Bot ? (
                (message._isPending ? (
                  <div className="text-disabled">
                    <AiIcon
                      isAnimated
                      className="align-middle mr-1"
                    />
                    Thinking...
                  </div>
                ) : (
                  <ReactMarkdown>
                    {message.content}
                  </ReactMarkdown>
                ))
              ) : (
                <div className="flex flex-col items-center gap-4">
                  <QuestionDate>
                    {format(parseISO(message.createdAt), 'PP')}
                  </QuestionDate>
                  <Question>
                    {message.content}
                  </Question>
                </div>
              )}
            </li>
          ))}
        </ul>
      </InfiniteScroll>
      <div className="py-5 bg-primary dark:bg-grey-900 flex-none px-5">
        <QuestionInput
          autoFocus
          placeholder="Ask follow-up"
          onKeyDown={async (e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const question = e.currentTarget.value.trim();
              if (!question) return;
              e.currentTarget.value = '';
              await ask({
                docId,
                question,
                conversationId,
                onSuccess: (questionId) => {
                  const element = document.querySelector<HTMLLIElement>(`[data-id="${questionId}"]`);
                  if (element) {
                    scrollIntoView(element);
                  }
                },
              });
            }
          }}
        />
      </div>
    </div>
  );
};
